import { useEffect } from 'react';

const DEFAULT_OG_IMAGE = 'https://statarena.app/og-image.png';

const upsertMeta = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

export const useSeo = ({
  title,
  description,
  canonical,
  robots,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage = DEFAULT_OG_IMAGE,
  twitterTitle,
  twitterDescription,
  twitterImage = DEFAULT_OG_IMAGE,
  twitterCard = 'summary_large_image',
  structuredData,
}) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      upsertMeta('meta[name="description"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        return meta;
      }).setAttribute('content', description);
    }

    if (robots) {
      upsertMeta('meta[name="robots"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        return meta;
      }).setAttribute('content', robots);
    }

    if (canonical) {
      upsertMeta('link[rel="canonical"]', () => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        return link;
      }).setAttribute('href', canonical);
    }

    const ogPairs = [
      ['og:title', ogTitle || title],
      ['og:description', ogDescription || description],
      ['og:url', ogUrl || canonical],
      ['og:image', ogImage],
    ];

    ogPairs.forEach(([property, value]) => {
      if (!value) return;
      upsertMeta(`meta[property="${property}"]`, () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', property);
        return meta;
      }).setAttribute('content', value);
    });

    const twitterPairs = [
      ['twitter:card', twitterCard],
      ['twitter:title', twitterTitle || ogTitle || title],
      ['twitter:description', twitterDescription || ogDescription || description],
      ['twitter:image', twitterImage || ogImage],
    ];

    twitterPairs.forEach(([name, value]) => {
      if (!value) return;
      upsertMeta(`meta[name="${name}"]`, () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', name);
        return meta;
      }).setAttribute('content', value);
    });

    const structuredDataId = 'page-structured-data';
    const existingStructuredData = document.getElementById(structuredDataId);
    if (structuredData) {
      const script = existingStructuredData || document.createElement('script');
      script.id = structuredDataId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      if (!existingStructuredData) {
        document.head.appendChild(script);
      }
    } else if (existingStructuredData) {
      existingStructuredData.remove();
    }
  }, [
    canonical,
    description,
    ogDescription,
    ogImage,
    ogTitle,
    ogUrl,
    robots,
    title,
    twitterDescription,
    twitterCard,
    twitterImage,
    twitterTitle,
    structuredData,
  ]);
};

export default useSeo;
